import { useSearch } from "@tanstack/react-router";
import { useMemo } from "react";
import { useProjectPermissions } from "@/app/capabilities/api/hooks";
import { PROJECT } from "@/app/capabilities/permissions";
import { ListLayout } from "@/components/custom/list-layout/list-layout";
import { EmptyResourceState } from "@/components/custom/resources/empty-resource-state";
import { DataTable } from "@/components/custom/table/data-table";
import { DataTableContent } from "@/components/custom/table/data-table-content";
import { DataTablePagination } from "@/components/custom/table/data-table-pagination";
import { useGetConnectedAccounts } from "../api/hooks";
import { AccountsTableToolbar } from "../details/connected-accounts/accounts-table-toolbar";
import { getConnectedAccountsColumns } from "../details/connected-accounts/columns";
import type { ConnectedAccountItem } from "../types";

const ConnectedAccountsTable = ({
  items,
}: {
  items: ConnectedAccountItem[];
}) => {
  const { canProject } = useProjectPermissions();
  const columns = useMemo(
    () =>
      getConnectedAccountsColumns(canProject(PROJECT.userConnections.delete)),
    [canProject]
  );

  return (
    <DataTable className="my-4 space-y-4" columns={columns} data={items}>
      <AccountsTableToolbar />
      <DataTableContent />
      <DataTablePagination />
    </DataTable>
  );
};

export const AccountsTable = () => {
  const searchParams = useSearch({
    from: "/_authenticated/orgs/$organizationId/projects/$projectId/auth/connected-users",
  });
  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  const name = (searchParams as { name?: string })?.name;

  // Fetch all connected accounts for that project
  const { data: allConnectedAccounts = [] } =
    useGetConnectedAccounts("project");

  const filteredConnectedAccounts = useMemo(() => {
    if (!allConnectedAccounts?.length) {
      return [];
    }

    const normalizedSearchTerm = name?.toLowerCase().trim() || "";

    const filterBySearch = (item: ConnectedAccountItem) => {
      if (!normalizedSearchTerm) {
        return true;
      }
      return (
        item.id.toLowerCase().includes(normalizedSearchTerm) ||
        item.user_id.toLowerCase().includes(normalizedSearchTerm) ||
        item.provider_id.toLowerCase().includes(normalizedSearchTerm) ||
        item.provider_description.toLowerCase().includes(normalizedSearchTerm)
      );
    };

    return allConnectedAccounts.filter(filterBySearch);
  }, [allConnectedAccounts, name]);

  return (
    <ListLayout
      description="View and manage connected users across all environments"
      title="Connected users"
    >
      {filteredConnectedAccounts && filteredConnectedAccounts.length > 0 ? (
        <ConnectedAccountsTable items={filteredConnectedAccounts} />
      ) : (
        <EmptyResourceState
          description="Connected users represent authenticated accounts that have authorized access to your application through various OAuth providers."
          resourceType="connected users"
        />
      )}
    </ListLayout>
  );
};
